import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Building2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const headers = () => { const t = localStorage.getItem("cauldron_token"); return t ? { Authorization: `Bearer ${t}` } : {}; };

/** My orgs list + create org form (Org tier gated). */
export function OrgsList() {
  const [orgs, setOrgs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", description: "" });
  const [gated, setGated] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/orgs/mine`, { headers: headers() });
      setOrgs(r.data.orgs || []);
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await axios.post(`${API}/orgs`, form, { headers: headers() });
      toast.success("Organization created");
      setForm({ name: "", slug: "", description: "" });
      setCreating(false);
      load();
    } catch (e) {
      if (e.response?.status === 403) setGated(true);
      else toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6" data-testid="orgs-list">
        <div className="flex justify-between mb-6">
          <div>
            <h1 className="text-3xl font-serif text-amber-100">Organizations</h1>
            <p className="text-amber-200/60 text-sm">Manage temples, houses, and multi-practitioner groups.</p>
          </div>
          <Button onClick={() => setCreating(true)} data-testid="create-org">+ New org</Button>
        </div>
        {gated && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-950/30 p-6 mb-6 text-center">
            <div className="text-lg text-amber-100 mb-2">Organizations are on the $149/mo Organization plan</div>
            <Link to="/pricing" className="text-amber-400 underline text-sm">See Organization plan →</Link>
          </div>
        )}
        {creating && !gated && (
          <div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-4 mb-6" data-testid="new-org-form">
            <Input placeholder="Org name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="org-name" />
            <Input className="mt-2" placeholder="url-slug (letters, numbers, -)" value={form.slug} onChange={e => setForm({...form, slug: e.target.value})} data-testid="org-slug" />
            <Textarea className="mt-2" placeholder="Short description" rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="org-desc" />
            <div className="flex gap-2 mt-3">
              <Button onClick={create} disabled={!form.name || !form.slug} data-testid="save-org">Create</Button>
              <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            </div>
          </div>
        )}
        {orgs.length === 0 && !creating ? (
          <div className="text-amber-200/60 py-12 text-center text-sm">You are not in any organizations yet.</div>
        ) : (
          <ul className="space-y-2">
            {orgs.map(o => (
              <li key={o.id}>
                <Link to={`/orgs/${o.id}`} className="block p-4 rounded-lg border border-neutral-800 bg-neutral-900/40 hover:bg-neutral-900/70 transition" data-testid={`org-${o.id}`}>
                  <div className="flex items-center gap-3">
                    <Building2 className="h-5 w-5 text-amber-400" />
                    <div>
                      <div className="text-amber-100 font-serif">{o.name}</div>
                      <div className="text-xs text-amber-200/50">{o.seatCount} seat{o.seatCount === 1 ? "" : "s"} · you're {o.myRole}</div>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}

/** Single org dashboard — members list, invite, calendar preview. */
export function OrgDashboard() {
  const { oid } = useParams();
  const [org, setOrg] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [calendar, setCalendar] = useState([]);
  const [myTier, setMyTier] = useState(null);

  const load = async () => {
    try {
      const [o, c, t] = await Promise.all([
        axios.get(`${API}/orgs/${oid}`, { headers: headers() }),
        axios.get(`${API}/orgs/${oid}/calendar?days=30`, { headers: headers() }),
        axios.get(`${API}/tiers/me`, { headers: headers() }),
      ]);
      setOrg(o.data);
      setCalendar(c.data.bookings || []);
      setMyTier(t.data);
    } catch (e) { toast.error("Failed to load org"); }
  };
  useEffect(() => { load(); }, [oid]);

  const startOrgBilling = async () => {
    try {
      const r = await axios.post(
        `${API}/tiers/checkout`,
        { origin: window.location.origin, tier: "org" },
        { headers: headers() }
      );
      if (r.data.url) window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed"); }
  };

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      await axios.post(`${API}/orgs/${oid}/invite`, { email: inviteEmail, role: "practitioner" }, { headers: headers() });
      toast.success("Invited");
      setInviteEmail("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (uid) => {
    if (!window.confirm("Remove this member?")) return;
    try {
      await axios.delete(`${API}/orgs/${oid}/members/${uid}`, { headers: headers() });
      load();
    } catch { toast.error("Failed"); }
  };

  if (!org) return <Layout><div className="p-8 text-amber-200/60">Loading…</div></Layout>;
  const isOwner = org.myRole === "owner";
  const onOrgTier = myTier?.tier === "org";

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="org-dashboard">
        <header>
          <h1 className="text-3xl font-serif text-amber-100">{org.name}</h1>
          <div className="text-sm text-amber-200/60">/{org.slug} · {org.seatCount} seat{org.seatCount === 1 ? "" : "s"}</div>
          {org.description && <p className="text-amber-100/80 mt-2">{org.description}</p>}
        </header>

        {/* Billing card (owner only) */}
        {isOwner && (
          <section
            className={`rounded-lg border p-4 ${onOrgTier ? "border-emerald-800/40 bg-emerald-950/20" : "border-amber-500/40 bg-amber-950/20"}`}
            data-testid="org-billing"
          >
            <div className="flex justify-between items-start gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-amber-300/60 mb-1">Billing</div>
                <h2 className="text-amber-100 font-serif">
                  {onOrgTier ? "Organization plan · $149/mo · ACTIVE" : "Fund this Organization"}
                </h2>
                <p className="text-sm text-amber-200/70 mt-1">
                  {onOrgTier
                    ? `1% platform fee on all seat bookings · ${org.seatCount} of unlimited seats used`
                    : "This org needs a paid Organization plan to unlock team seats and shared calendar."}
                </p>
              </div>
              {!onOrgTier && (
                <button
                  onClick={startOrgBilling}
                  className="px-4 py-2 rounded-md bg-amber-500 hover:bg-amber-400 text-neutral-900 font-medium text-sm whitespace-nowrap"
                  data-testid="start-org-billing"
                >
                  Subscribe · $149/mo
                </button>
              )}
            </div>
          </section>
        )}

        <section className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-4">
          <h2 className="text-amber-100 font-serif mb-3">Team ({org.members.length})</h2>
          {isOwner && (
            <div className="flex gap-2 mb-4">
              <Input placeholder="Invite by practitioner email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} data-testid="invite-email" />
              <Button onClick={invite} data-testid="send-invite">Invite</Button>
            </div>
          )}
          <ul className="space-y-2" data-testid="members-list">
            {org.members.map(m => (
              <li key={m.userId} className="flex justify-between items-center p-3 rounded-md border border-neutral-800/60" data-testid={`member-${m.userId}`}>
                <div>
                  <div className="text-amber-100 text-sm">{m.name}</div>
                  <div className="text-xs text-amber-200/50">{m.email} · {m.role} · {m.status}</div>
                </div>
                {isOwner && m.role !== "owner" && (
                  <button onClick={() => remove(m.userId)} className="text-red-400 text-xs">Remove</button>
                )}
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-lg border border-neutral-800 bg-neutral-900/40 p-4">
          <h2 className="text-amber-100 font-serif mb-3">Shared calendar (next 30 days)</h2>
          {calendar.length === 0 ? (
            <div className="text-amber-200/50 text-sm py-6 text-center">No upcoming bookings across seats.</div>
          ) : (
            <ul className="space-y-2" data-testid="calendar-list">
              {calendar.slice(0, 20).map(b => (
                <li key={b.id} className="flex justify-between items-center p-2 rounded-md border border-neutral-800/40 text-sm">
                  <div>
                    <div className="text-amber-100">{b.scheduledAt ? new Date(b.scheduledAt).toLocaleString() : "—"}</div>
                    <div className="text-xs text-amber-200/50">Provider {b.providerId?.slice(0,8)} · {b.status}</div>
                  </div>
                  <div className="text-amber-200/70">${(b.amount || 0).toFixed(2)}</div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Layout>
  );
}
